/** Default copy when the real message looks like a developer/runtime error. */
export const DEFAULT_USER_FACING_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/**
 * True when the message is generic API/HTTP jargon (e.g. FastAPI default 500 body, status line).
 * These should be replaced with {@link DEFAULT_USER_FACING_ERROR_MESSAGE} for UI.
 */
export function looksLikeGenericServerFailureMessage(message: string): boolean {
  const t = message.trim();
  if (!t) return true;
  const lower = t.toLowerCase();
  if (lower === 'internal server error') return true;
  if (lower.startsWith('internal server error:') || lower.startsWith('internal server error ')) {
    return true;
  }
  // Axios when no friendly body is parsed
  if (/^request failed with status code \d{3}$/i.test(t)) return true;
  // Built in throwApiError / status line patterns
  if (/^api error \d{3}:\s*internal server error$/i.test(t)) return true;
  // Auth token verification details should not be shown directly to end users.
  if (
    /^api error (401|403):.*\b(invalid|expired)\b.*\b(token|jwt|bearer|authorization)\b/i.test(t)
  ) {
    return true;
  }
  if (
    /^(?:auth(?:entication|orization)?(?:\s+error)?[:\s-]\s*)?(?:invalid|expired)\s+(?:access\s+)?(?:auth\s+)?(?:jwt\s+)?token\b/i.test(
      t,
    )
  ) {
    return true;
  }
  if (/^token(?:\s+is)?\s+(?:invalid|expired)\b/i.test(t)) return true;
  // Network/Timeout jargon (last-resort redaction for unknown strings)
  if (/\btimeout\b.*\bexceeded\b/i.test(t)) return true;
  if (/\beconnaborted\b/i.test(t)) return true;
  return false;
}

/**
 * Replace generic "Internal server error" and similar with a single friendly line.
 */
export function sanitizeGenericServerFailureMessage(
  message: string,
  fallback: string = DEFAULT_USER_FACING_ERROR_MESSAGE,
): string {
  return looksLikeGenericServerFailureMessage(message) ? fallback : message;
}

/**
 * Full pipeline for strings shown in toasts, inline errors, and support bundles:
 * generic 500/API jargon → then JS/runtime jargon redaction.
 */
export function toUserFacingErrorMessage(
  message: string,
  fallback: string = DEFAULT_USER_FACING_ERROR_MESSAGE,
): string {
  return redactTechnicalErrorMessage(
    sanitizeGenericServerFailureMessage(message, fallback),
    fallback,
  );
}

/**
 * Detect strings that look like JavaScript / browser runtime errors.
 * These should never be shown to end users as the primary error copy.
 */
export function looksLikeTechnicalRuntimeMessage(message: string): boolean {
  const t = message.trim();
  if (!t) return false;
  return (
    /\bis not defined\b/i.test(t) ||
    /\bis not a function\b/i.test(t) ||
    /\bReferenceError\b/i.test(t) ||
    /\bTypeError\b/i.test(t) ||
    /\bSyntaxError\b/i.test(t) ||
    /Cannot read properties of undefined\b/i.test(t) ||
    /^Uncaught\b/i.test(t)
  );
}

/**
 * Replace technical runtime messages with a safe fallback for UI/copy.
 */
export function redactTechnicalErrorMessage(message: string, fallback: string): string {
  return looksLikeTechnicalRuntimeMessage(message) ? fallback : message;
}
